"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.InsightView = exports.IntlInsightView = void 0;
var tslib_1 = require("tslib");
// (C) 2019 GoodData Corporation
var react_1 = tslib_1.__importStar(require("react"));
var react_intl_1 = require("react-intl");
var sdk_model_1 = require("@gooddata/sdk-model");
var sdk_ui_1 = require("@gooddata/sdk-ui");
var InsightTitle_1 = tslib_1.__importDefault(require("./InsightTitle"));
var InsightRenderer_1 = require("./InsightRenderer");
var InsightError_1 = require("./InsightError");
var dataLoaders_1 = require("../dataLoaders");
var InsightViewCore = function (props) {
    var insight = props.insight, backend = props.backend, workspace = props.workspace, filters = props.filters, executeByReference = props.executeByReference, showTitle = props.showTitle, colorPalette = props.colorPalette, config = props.config, execConfig = props.execConfig, locale = props.locale, drillableItems = props.drillableItems, onDrill = props.onDrill, onLoadingChanged = props.onLoadingChanged, onExportReady = props.onExportReady, onError = props.onError, onInsightLoaded = props.onInsightLoaded, pushData = props.pushData, _a = props.ErrorComponent, ErrorComponent = _a === void 0 ? sdk_ui_1.ErrorComponent : _a, _b = props.LoadingComponent, LoadingComponent = _b === void 0 ? sdk_ui_1.LoadingComponent : _b, _c = props.TitleComponent, TitleComponent = _c === void 0 ? InsightTitle_1.default : _c;
    var _d = react_1.useState({
        isVisualizationLoading: false,
        visualizationError: undefined,
    }), state = _d[0], setState = _d[1];
    // ref of the insight last reported by the onInsightLoaded
    var lastReportedRef = react_1.useRef();
    var _e = sdk_ui_1.useCancelablePromise({
        promise: function () { return tslib_1.__awaiter(void 0, void 0, void 0, function () {
            var ref, insightData;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        ref = typeof insight === "string" ? sdk_model_1.idRef(insight, "insight") : insight;
                        return [4 /*yield*/, dataLoaders_1.insightDataLoaderFactory
                                .forWorkspace(workspace)
                                .getInsight(backend, ref)];
                    case 1:
                        insightData = _a.sent();
                        if (!lastReportedRef.current ||
                            !sdk_model_1.areObjRefsEqual(lastReportedRef.current, sdk_model_1.insightRef(insightData))) {
                            onInsightLoaded === null || onInsightLoaded === void 0 ? void 0 : onInsightLoaded(insightData);
                            lastReportedRef.current = sdk_model_1.insightRef(insightData);
                        }
                        if (executeByReference) {
                            /*
                             * In execute-by-reference, filter merging happens on the server
                             */
                            return [2 /*return*/, insightData];
                        }
                        /*
                         * In freeform execution, frontend is responsible for filter merging. Code defers the merging to the
                         * implementation of analytical backend because the merging may first need to unify how the different
                         * filter entities are referenced (id vs uri).
                         */
                        return [2 /*return*/, backend
                                .workspace(workspace)
                                .insights()
                                .getInsightWithAddedFilters(insightData, filters !== null && filters !== void 0 ? filters : [])];
                }
            });
        }); },
    }, [insight, backend, workspace, executeByReference, filters, onInsightLoaded]), insightError = _e.error, insightResult = _e.result, insightStatus = _e.status;
    var _f = sdk_ui_1.useCancelablePromise({
        promise: function () {
            return dataLoaders_1.colorPaletteDataLoaderFactory.forWorkspace(workspace).getColorPalette(backend);
        },
    }, [backend, workspace]), colorPaletteError = _f.error, colorPaletteResult = _f.result, colorPaletteStatus = _f.status;
    var _g = sdk_ui_1.useCancelablePromise({
        promise: function () {
            return dataLoaders_1.userWorkspaceSettingsDataLoaderFactory
                .forWorkspace(workspace)
                .getUserWorkspaceSettings(backend);
        },
    }, [backend, workspace]), workspaceSettingsError = _g.error, workspaceSettingsResult = _g.result, workspaceSettingsStatus = _g.status;
    // extract the url outside of backendWithTelemetry and use it as a dependency instead of the whole insight
    // this reduces the amount of re-renders in case just filters change for example
    var currentInsightVisualizationUrl = insightResult && sdk_model_1.insightVisualizationUrl(insightResult);
    var backendWithTelemetry = react_1.useMemo(function () {
        var telemetryProps = tslib_1.__assign({}, props);
        // add a fake prop so that the type of the visualization rendered is present in the telemetry
        if (currentInsightVisualizationUrl) {
            var key = "visualizationUrl_" + currentInsightVisualizationUrl;
            telemetryProps[key] = true;
        }
        return backend.withTelemetry("InsightView", telemetryProps);
    }, [currentInsightVisualizationUrl, backend]);
    var handleLoadingChanged = react_1.useCallback(function (_a) {
        var isLoading = _a.isLoading;
        setState(function (oldState) {
            return {
                isVisualizationLoading: isLoading,
                // if we started loading, any previous vis error is obsolete at this point, get rid of it
                visualizationError: isLoading ? undefined : oldState.visualizationError,
            };
        });
        onLoadingChanged === null || onLoadingChanged === void 0 ? void 0 : onLoadingChanged({ isLoading: isLoading });
    }, [onLoadingChanged]);
    var handleError = react_1.useCallback(function (visualizationError) {
        setState(function (oldState) {
            return tslib_1.__assign(tslib_1.__assign({}, oldState), { visualizationError: visualizationError });
        });
        onError === null || onError === void 0 ? void 0 : onError(visualizationError);
    }, [onError]);
    var isDataLoading = insightStatus === "loading" ||
        insightStatus === "pending" ||
        colorPaletteStatus === "loading" ||
        colorPaletteStatus === "pending" ||
        workspaceSettingsStatus === "loading" ||
        workspaceSettingsStatus === "pending";
    var resolveInsightTitle = function (insight) {
        switch (typeof showTitle) {
            case "string":
                return showTitle;
            case "boolean":
                return !isDataLoading && showTitle && insight ? sdk_model_1.insightTitle(insight) : undefined;
            case "function":
                return !isDataLoading && insight && showTitle(insight);
            default:
                return undefined;
        }
    };
    var resolvedTitle = resolveInsightTitle(insightResult);
    var isLoadingShown = isDataLoading || state.isVisualizationLoading;
    var error = state.visualizationError || insightError || colorPaletteError || workspaceSettingsError;
    return (react_1.default.createElement("div", { className: "insight-view-container" },
        resolvedTitle && react_1.default.createElement(TitleComponent, { title: resolvedTitle }),
        isLoadingShown && react_1.default.createElement(LoadingComponent, { className: "insight-view-loader" }),
        error && !isDataLoading && (react_1.default.createElement(InsightError_1.InsightError, { error: sdk_ui_1.convertError(error), ErrorComponent: ErrorComponent })),
        react_1.default.createElement("div", { className: "insight-view-visualization", 
            // make the visualization div 0 height so that the loading component can take up the whole area
            style: isLoadingShown ? { height: 0 } : undefined },
            react_1.default.createElement(InsightRenderer_1.InsightRenderer, { insight: insightResult, workspace: workspace, backend: backendWithTelemetry, colorPalette: colorPalette !== null && colorPalette !== void 0 ? colorPalette : colorPaletteResult, config: config, execConfig: execConfig, drillableItems: drillableItems, executeByReference: executeByReference, filters: filters, locale: locale || (workspaceSettingsResult === null || workspaceSettingsResult === void 0 ? void 0 : workspaceSettingsResult.locale) || sdk_ui_1.DefaultLocale, settings: workspaceSettingsResult, ErrorComponent: ErrorComponent, LoadingComponent: LoadingComponent, onDrill: onDrill, onError: handleError, onExportReady: onExportReady, onLoadingChanged: handleLoadingChanged, pushData: pushData }))));
};
exports.IntlInsightView = sdk_ui_1.withContexts(react_intl_1.injectIntl(InsightViewCore));
/**
 * Renders insight which was previously created and saved in the Analytical Designer.
 *
 * @public
 */
var InsightView = /** @class */ (function (_super) {
    tslib_1.__extends(InsightView, _super);
    function InsightView() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    InsightView.prototype.render = function () {
        return (react_1.default.createElement(sdk_ui_1.IntlWrapper, { locale: this.props.locale },
            react_1.default.createElement(exports.IntlInsightView, tslib_1.__assign({}, this.props))));
    };
    return InsightView;
}(react_1.default.Component));
exports.InsightView = InsightView;
//# sourceMappingURL=InsightView.js.map